import { Component, Input, OnInit, signal, ViewChildren, QueryList, ElementRef, AfterViewInit, Output, EventEmitter, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OtpService } from '../../../service/otp.service';
import { Elate } from '../../../model/elate.model';

/**
 * Composant de vérification email par code OTP pour FlashLevel (Elate).
 * Utilise l'API externe (sans auth, par token Elate).
 *
 * Usage :
 * <lib-ai-exam-onboarding-otp
 *   [elate]="elate"
 *   (verificationComplete)="onOtpVerified()">
 * </lib-ai-exam-onboarding-otp>
 */
@Component({
  selector: 'lib-ai-exam-onboarding-otp',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './AI-exam-onboarding-otp.component.html',
  styleUrls: ['./AI-exam-onboarding-otp.component.scss'],
})
export class AIExamOnboardingOtpComponent implements OnInit, AfterViewInit {
  @Input() elate!: Elate;
  @Output() verificationComplete = new EventEmitter<void>();

  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private otpService = inject(OtpService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);

  loading = signal(false);
  codeVerified = signal(false);
  errorMessage = signal('');
  successMessage = signal('');
  codeDigits = ['', '', '', '', '', ''];
  otpSent = signal(false);
  resendMessage = signal('');

  ngOnInit(): void {
    this.sendOTP();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const firstInput = this.codeInputs?.get(0);
      if (firstInput) {
        firstInput.nativeElement.focus();
      }
    }, 100);
  }

  sendOTP(): void {
    if (!this.elate?.token) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    this.otpService.sendOTPExternal(this.elate.token, { site_id: 2 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.otpSent.set(true);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set(this.translate.instant('ai-exam-onboarding.otp.error-send'));
          this.loading.set(false);
        }
      });
  }

  onFocus(index: number, event: Event): void {
    const firstEmptyIndex = this.codeDigits.findIndex(digit => digit === '');
    if (firstEmptyIndex !== -1 && index !== firstEmptyIndex) {
      event.preventDefault();
      const targetInput = this.codeInputs.get(firstEmptyIndex);
      if (targetInput) {
        targetInput.nativeElement.focus();
      }
    }
  }

  onInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length > 0) {
      const digit = value.slice(-1);
      if (/^\d$/.test(digit)) {
        this.codeDigits[index] = digit;
        input.value = digit;

        if (index < 5) {
          const nextInput = this.codeInputs.get(index + 1);
          if (nextInput) {
            nextInput.nativeElement.focus();
          }
        } else {
          this.autoVerify();
        }
      } else {
        input.value = '';
        this.codeDigits[index] = '';
      }
    } else {
      this.codeDigits[index] = '';
    }
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (input.value === '' && index > 0) {
        event.preventDefault();
        this.codeDigits[index - 1] = '';
        const prevInput = this.codeInputs.get(index - 1);
        if (prevInput) {
          prevInput.nativeElement.value = '';
          prevInput.nativeElement.focus();
        }
      } else if (input.value !== '') {
        this.codeDigits[index] = '';
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const prevInput = this.codeInputs.get(index - 1);
      if (prevInput) {
        prevInput.nativeElement.focus();
      }
    } else if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      const nextInput = this.codeInputs.get(index + 1);
      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');

    digits.forEach((digit, i) => {
      if (i < 6) {
        this.codeDigits[i] = digit;
        const input = this.codeInputs.get(i);
        if (input) {
          input.nativeElement.value = digit;
        }
      }
    });

    if (digits.length === 6) {
      this.autoVerify();
    } else {
      const focusIndex = Math.min(digits.length, 5);
      const targetInput = this.codeInputs.get(focusIndex);
      if (targetInput) {
        targetInput.nativeElement.focus();
      }
    }
  }

  private autoVerify(): void {
    const code = this.codeDigits.join('');
    if (code.length === 6) {
      setTimeout(() => {
        this.verifyCode();
      }, 200);
    }
  }

  verifyCode(): void {
    const code = this.codeDigits.join('');

    if (code.length !== 6) {
      this.errorMessage.set(this.translate.instant('ai-exam-onboarding.otp.error-6digits'));
      return;
    }

    if (!this.elate?.token) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    this.otpService.verifyOTPExternal(this.elate.token, { code })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.verified) {
            this.codeVerified.set(true);
            this.successMessage.set(this.translate.instant('ai-exam-onboarding.otp.success'));
            this.loading.set(false);
            setTimeout(() => {
              this.verificationComplete.emit();
            }, 500);
          } else {
            this.errorMessage.set(this.translate.instant('ai-exam-onboarding.otp.error-invalid'));
            this.clearCode();
            this.loading.set(false);
          }
        },
        error: () => {
          this.errorMessage.set(this.translate.instant('ai-exam-onboarding.otp.error-expired'));
          this.clearCode();
          this.loading.set(false);
        }
      });
  }

  resendCode(): void {
    this.codeVerified.set(false);
    this.clearCode();
    this.errorMessage.set('');
    this.successMessage.set('');
    this.resendMessage.set('');
    this.loading.set(true);

    this.otpService.sendOTPExternal(this.elate.token, { site_id: 2 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resendMessage.set(this.translate.instant('ai-exam-onboarding.otp.resend-success'));
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set(this.translate.instant('ai-exam-onboarding.otp.error-send'));
          this.loading.set(false);
        }
      });
  }

  private clearCode(): void {
    this.codeDigits = ['', '', '', '', '', ''];
    this.codeInputs?.forEach((input) => {
      input.nativeElement.value = '';
    });
    const firstInput = this.codeInputs?.get(0);
    if (firstInput) {
      firstInput.nativeElement.focus();
    }
  }
}
